import React from "react";
import { Helmet } from "react-helmet-async";
import { absUrl, buildTitle, localBusinessJsonLd } from "../../lib/seo";
import { useGoogleReviews } from "../../lib/googleReviews";

/**
 * Per-page SEO helmet block. Adds title, description, canonical, OG/Twitter
 * tags, and (by default) the site-wide LocalBusiness JSON-LD. Pages can
 * pass additional structuredData (array of objects).
 *
 * Canonical/og:url are derived from window.location.origin via absUrl(), so
 * they always match the host the visitor is on (preview hosts + production).
 *
 * Phase 9 hardening:
 *   - When `noindex` is true, we also skip the canonical link AND og:url meta
 *     (canonical on a noindexed page is meaningless and can confuse crawlers).
 *   - `noCanonical` can also be passed explicitly to suppress canonical/og:url
 *     even when the page IS indexable (used by paid-landing duplicates).
 *   - We only emit <meta name="robots"> when restricting indexing.
 *     Indexable pages omit the tag entirely — "index,follow" is the default
 *     for robots and the tag adds noise without value.
 *
 * Phase 28 — share-card images. Messenger / Facebook / WhatsApp / LinkedIn
 * scrape the (prerendered) HTML and need an ABSOLUTE og:image of at least
 * 200×200 in JPEG/PNG/GIF. The old default was the 184×42 transparent logo
 * PNG and some pages passed relative .webp paths — both rejected, so shares
 * had no preview image. Now:
 *   - default image is /og-default.jpg (1200×630 JPEG) with width/height
 *   - relative paths are made absolute
 *   - .webp / .svg / .avif images fall back to the default (Facebook does
 *     not reliably render them in link previews)
 */

export const OG_DEFAULT_IMAGE_PATH = "/og-default.jpg";
const OG_DEFAULT_WIDTH = 1200;
const OG_DEFAULT_HEIGHT = 630;
const UNSUPPORTED_IMAGE_RE = /\.(webp|svg|avif|heic|heif)(\?.*)?$/i;

/** Resolve a page-supplied image into a share-safe absolute URL. */
export const resolveOgImage = (image) => {
  const raw = (image || "").trim();
  if (!raw || UNSUPPORTED_IMAGE_RE.test(raw)) {
    return { url: absUrl(OG_DEFAULT_IMAGE_PATH), isDefault: true };
  }
  if (/^https?:\/\//i.test(raw)) return { url: raw, isDefault: false };
  if (raw.startsWith("//")) return { url: `https:${raw}`, isDefault: false };
  return { url: absUrl(raw.startsWith("/") ? raw : `/${raw}`), isDefault: false };
};

export const SEO = ({
  title,
  description,
  path,
  image,
  imageAlt,
  prevUrl,
  nextUrl,
  ogTitle,
  twitterTitle,
  noindex = false,
  noCanonical = false,
  structuredData = [],
  type = "website",
}) => {
  // Phase 29 — subscribe to the live Google review numbers so the JSON-LD
  // aggregateRating re-renders once they arrive (value read inside
  // localBusinessJsonLd()).
  useGoogleReviews();
  const fullTitle = buildTitle(title);
  const canonical = absUrl(path);
  const og = resolveOgImage(image);
  const ogImageAlt = imageAlt || (og.isDefault ? "QVolt Solar — Queensland's family-owned solar installer" : fullTitle);
  const blocks = [localBusinessJsonLd(), ...structuredData];
  const suppressCanonical = noindex || noCanonical;
  // When an explicit ogTitle/twitterTitle is passed, emit it raw (no brand
  // suffix). When omitted, fall back to the full <title> for consistency.
  const ogTitleFinal = ogTitle || fullTitle;
  const twitterTitleFinal = twitterTitle || fullTitle;
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {!suppressCanonical && <link rel="canonical" href={canonical} />}
      {prevUrl && <link rel="prev" href={prevUrl} />}
      {nextUrl && <link rel="next" href={nextUrl} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="QVolt Solar" />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:title" content={ogTitleFinal} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={og.url} />
      {og.url.startsWith("https://") && <meta property="og:image:secure_url" content={og.url} />}
      {og.isDefault && <meta property="og:image:type" content="image/jpeg" />}
      {og.isDefault && <meta property="og:image:width" content={String(OG_DEFAULT_WIDTH)} />}
      {og.isDefault && <meta property="og:image:height" content={String(OG_DEFAULT_HEIGHT)} />}
      <meta property="og:image:alt" content={ogImageAlt} />
      {!suppressCanonical && <meta property="og:url" content={canonical} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={twitterTitleFinal} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={og.url} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(b)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
